import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  useMediaQuery,
  useTheme,
  Typography
} from '@mui/material';

interface Column {
  id: string;
  label: string;
  render: (lead: any, index?: number) => React.ReactNode;
  minWidth?: number;
  sortable?: boolean;
  sortFn?: (a: any, b: any) => number;
}

interface LeadsTableProps {
  leads: any[];
  columns: Column[];
  totalProfiles: number;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  selectedUsernames?: string[];
  onSelectLead?: (username: string, checked: boolean) => void;
  onSelectAll?: (checked: boolean) => void;
  showCheckbox?: boolean;
  sortConfig?: { sortBy: string | null; sortOrder: 'asc' | 'desc' };
  onSortChange?: (columnId: string) => void;
  highlightCondition?: (lead: any) => boolean;
}

const LeadsTable: React.FC<LeadsTableProps> = ({
  leads,
  columns,
  totalProfiles,
  currentPage,
  setCurrentPage,
  selectedUsernames = [],
  onSelectLead,
  onSelectAll,
  showCheckbox = true,
  sortConfig,
  onSortChange,
  highlightCondition,
}) => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery('(min-width:1300px)');
  const isMediumScreen = useMediaQuery('(min-width:900px)');
  const totalPages = Math.ceil(totalProfiles / 10);

  const sortedLeads = leads;

  const handleSortClick = (column: Column) => {
    if (!column.sortable || !onSortChange) return;
    onSortChange(column.id);
  };

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      className="table-responsive border rounded"
    >
      <Table className="table table-sm mb-0">
        <TableHead>
          <TableRow>
            {showCheckbox && onSelectAll && (
              <TableCell
                className="align-middle text-nowrap px-2 py-2"
              >
                <Checkbox
                  indeterminate={selectedUsernames.length > 0 && selectedUsernames.length < leads.length}
                  checked={leads.length > 0 && selectedUsernames.length === leads.length}
                  onChange={(e) => onSelectAll(e.target.checked)}
                  size="small"
                  sx={{
                    borderRadius: '4px',
                    border: '1px solid #D0D5DD',
                    boxShadow: 'none',
                    width: 16,
                    height: 16,
                    p: 0,
                    color: '#5175E0',
                    '&.Mui-checked': {
                      color: '#5175E0',
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: 16,
                    }
                  }}
                />
              </TableCell>
            )}
            {columns.map((column) => {
              const isSortedColumn = sortConfig?.sortBy === column.id;
              return (
                <TableCell
                  key={column.id}
                  className="align-middle text-nowrap px-2 py-2 small"
                  sx={{
                    cursor: column.sortable ? 'pointer' : 'default',
                    userSelect: 'none',
                    '&:hover .sort-icon': {
                      opacity: column.sortable ? 1 : 0,
                    }
                  }}
                  onClick={() => column.sortable && handleSortClick(column)}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontWeight: 600,
                    color: '#333'
                  }}>
                    <span>{column.label}</span>
                    {column.sortable && (
                      <span
                        className="sort-icon"
                        style={{
                          opacity: isSortedColumn ? 1 : 0.3,
                          fontSize: '12px',
                          transition: 'opacity 0.2s',
                          color: isSortedColumn ? '#5175e0' : '#666'
                        }}
                      >
                        {isSortedColumn ? (sortConfig!.sortOrder === 'asc' ? '▲' : '▼') : '▲▼'}
                      </span>
                    )}
                  </div>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        {leads.length === 0 ? (
          <TableBody>
            <TableRow>
              <td colSpan={columns.length + (showCheckbox ? 1 : 0)} className="text-center py-4 bg-light text-primary fw-medium">
                No record found
              </td>
            </TableRow>
          </TableBody>
        ) : (

          <TableBody>
            {sortedLeads.map((lead, index) => {
              const isHighlighted = highlightCondition ? highlightCondition(lead) : false;
              const isSelected = selectedUsernames.includes(lead.username);
              return (
                <TableRow
                  key={lead.username || index}
                  className={`${isHighlighted
                    ? "bg-success-subtle"
                    : isSelected
                      ? "bg-success-subtle border-success border border-2"
                      : index % 2 === 0
                        ? "bg-white"
                        : "bg-light"
                    }`}
                  style={isHighlighted ? {
                    borderLeft: '4px solid #2e7d32',
                    backgroundColor: '#e8f5e9',
                  } : undefined}
                >
                  {showCheckbox && onSelectLead && (
                    <TableCell
                      className="align-middle text-nowr ap px-2 py-2 small"
                    >
                      <Checkbox
                        checked={selectedUsernames.includes(lead.username)}
                        onChange={(e) => onSelectLead(lead.username, e.target.checked)}
                        size="small"
                        sx={{
                          borderRadius: '4px',
                          border: '1px solid #D0D5DD',
                          boxShadow: 'none',
                          width: 16,
                          height: 16,
                          p: 0,
                          color: '#5175E0',
                          '&.Mui-checked': {
                            color: '#5175E0',
                          },
                          '& .MuiSvgIcon-root': {
                            fontSize: 16,
                          }
                        }}
                      />
                    </TableCell>
                  )}
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      className="align-middle text-nowrap px-2 py-2 small"
                    >
                      {column.render(lead, index)}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        )}
      </Table>
      {totalProfiles > 10 && (
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 px-3 py-2">
          <div className="d-flex gap-3">
            <button
              className="btn btn-outline-secondary btn-sm rounded-pill px-3 py-1"
              onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <button
              className="btn btn-outline-secondary btn-sm rounded-pill px-3 py-1"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage * 10 >= totalProfiles}
            >
              Next
            </button>
          </div>
          <div className="small text-muted">
            Page {currentPage} of {totalPages}
          </div>
        </div>
      )}
    </TableContainer>
  );
};

export default LeadsTable;